<template>
  <div class="mx-2 mx-md-0">
    <!-- Mobile Filter Toggle -->
    <div
      v-if="isMobile"
      class="d-flex justify-content-between align-items-center mb-2 py-2"
    >
      <b-button
        size="sm"
        variant="outline-primary"
        @click="filtersVisible = !filtersVisible"
      >
        <b-icon-funnel-fill class="mr-1" />
        {{ filtersVisible ? "Hide Filters" : "Show Filters" }}
      </b-button>
    </div>

    <!-- Filters Section -->
    <div class="d-flex flex-column flex-lg-row" style="gap: 7px; width: 100%;">
      <!-- Date Range Picker -->
      <b-card style="display: inline-block; width: 100%;" :style="{'max-width': isMobile ? 'none' : '300px'}">
        <b-card-text>
          <div
            style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 5px;"
          >
            <span>DATES (Optional)</span>
            <div>
              <b-button
                size="sm"
                variant="dark"
                @click="onDateRangeChange"
                style="margin-right: 5px;"
              >
                SEARCH
              </b-button>
              <b-button
                v-if="date_range"
                size="sm"
                variant="outline-danger"
                @click="clearDateFilter"
              >
                CLEAR
              </b-button>
            </div>
          </div>
          <b-input
            id="dates"
            size="sm"
            v-model="date_range"
            placeholder="Select date range (optional)"
            style="width: 100%;"
            autocomplete="off"
            clearable
          ></b-input>
        </b-card-text>
      </b-card>

      <!-- Collapsible Filters -->
      <b-collapse :visible="!isMobile || filtersVisible" style="gap: 7px; width: 100%;">
        <!-- Search & Export -->
        <b-card
          style="display: inline-block; width: 100%; vertical-align: top;"
          :style="{'max-width': isMobile ? 'none' : '350px'}"
          v-if="items.length > 0"
        >
          <div>
            <div
              style="display: inline-block; vertical-align: top;"
              v-if="packet.userDetails && packet.userDetails.userrole == 'admin'"
            >
              <b-button variant="info" size="sm" @click="downloadExcel">
                DOWNLOAD EXCEL
              </b-button>
            </div>
          </div>
          <div style="margin-top: 5px;">
            <b-form-input
              v-model="search_txt"
              size="sm"
              placeholder="SEARCH IN RESULTS"
            ></b-form-input>
          </div>
        </b-card>

        <!-- Category Filter -->
        <b-card
          style="display: inline-block; width: 100%; vertical-align: top;"
          :style="{'max-width': isMobile ? 'none' : '240px'}"
          v-if="items.length > 0"
        >
          <div>FILTER CATEGORY</div>
          <div>
            <b-form-select
              :options="categoryOptions"
              v-model="picked_category"
              style="width: 100%;"
            >
              <template #first>
                <b-form-select-option :value="null">ALL</b-form-select-option>
              </template>
            </b-form-select>
          </div>
        </b-card>

        <!-- Vendor Filter -->
        <b-card
          style="display: inline-block; width: 100%; vertical-align: top;"
          :style="{'max-width': isMobile ? 'none' : '240px'}"
          v-if="items.length > 0"
        >
          <div>FILTER VENDOR</div>
          <div>
            <b-form-select
              :options="vendorOptions"
              v-model="picked_vendor"
              style="width: 100%;"
            >
              <template #first>
                <b-form-select-option :value="null">ALL</b-form-select-option>
              </template>
            </b-form-select>
          </div>
        </b-card>
      </b-collapse>
    </div>

    <!-- Results Table -->
    <b-card style="margin-top: 10px; min-height: 250px; max-height: 100%;">
      <div v-if="items_being_loaded">
        <loading :active="true" :can-cancel="false" :is-full-page="false">
        </loading>
      </div>

      <div v-else>
        <div v-if="items.length == 0">
          <b-alert variant="warning" show>
            NO NON-INVENTORY ITEMS FOUND
          </b-alert>
        </div>

        <div v-else style="overflow-x: auto; width: 100%;">
          <!-- Summary Stats -->
          <div class="summary-stats mb-3">
            <b-row>
              <b-col>
                <div class="stat-box">
                  <span class="stat-label">Total Items:</span>
                  <span class="stat-value">{{ filteredItems.length }}</span>
                </div>
              </b-col>
              <b-col>
                <div class="stat-box">
                  <span class="stat-label">Total Quantity:</span>
                  <span class="stat-value">{{ totalQuantity }}</span>
                </div>
              </b-col>
              <b-col>
                <div class="stat-box">
                  <span class="stat-label">Total Value:</span>
                  <span class="stat-value">${{ totalValue.toFixed(2) }}</span>
                </div>
              </b-col>
            </b-row>
          </div>

          <table style="border-collapse: collapse; width: 100%;">
            <thead>
              <tr>
                <th class="ipt_tdhr" @click="sortby('invoice_id')" style="min-width: 120px;">
                  INVOICE
                </th>
                <th class="ipt_tdhr" @click="sortby('product_sku')" style="min-width: 120px;">
                  SKU
                </th>
                <th class="ipt_tdhr" @click="sortby('product_name')" style="min-width: 200px;">
                  PRODUCT NAME
                </th>
                <th class="ipt_tdhr" @click="sortby('category_name')" style="min-width: 120px;">
                  CATEGORY
                </th>
                <th class="ipt_tdhr" @click="sortby('vendor')" style="min-width: 120px;">
                  VENDOR
                </th>
                <th class="ipt_tdhr" @click="sortby('quantity')" style="min-width: 80px;">
                  QTY
                </th>
                <th class="ipt_tdhr" @click="sortby('selling_price')" style="min-width: 100px;">
                  PRICE
                </th>
                <th class="ipt_tdhr" @click="sortby('total_price')" style="min-width: 120px;">
                  TOTAL
                </th>
                <th class="ipt_tdhr" @click="sortby('added_by')" style="min-width: 120px;">
                  ADDED BY
                </th>
                <th class="ipt_tdhr" @click="sortby('added_at')" style="min-width: 150px;">
                  ADDED AT
                </th>
                <th class="ipt_tdhr" style="min-width: 80px;">
                  IMAGE
                </th>
                <th class="ipt_tdhr" style="min-width: 100px;">
                  ACTIONS
                </th>
              </tr>
            </thead>

            <tbody>
              <!-- Summary Row -->
              <tr>
                <td class="ipt_tdhr" style="font-weight: normal; background-color: lightyellow;"></td>
                <td class="ipt_tdhr" style="font-weight: normal; background-color: lightyellow;"></td>
                <td class="ipt_tdhr" style="font-weight: normal; background-color: lightyellow;"></td>
                <td class="ipt_tdhr" style="font-weight: normal; background-color: lightyellow;"></td>
                <td class="ipt_tdhr" style="font-weight: normal; background-color: lightyellow;"></td>
                <td class="ipt_tdhr" style="font-weight: bold; background-color: lightyellow;">
                  {{ totalQuantity }}
                </td>
                <td class="ipt_tdhr" style="font-weight: normal; background-color: lightyellow;"></td>
                <td class="ipt_tdhr" style="font-weight: bold; background-color: lightyellow;">
                  ${{ totalValue.toFixed(2) }}
                </td>
                <td class="ipt_tdhr" style="font-weight: normal; background-color: lightyellow;"></td>
                <td class="ipt_tdhr" style="font-weight: normal; background-color: lightyellow;"></td>
                <td class="ipt_tdhr" style="font-weight: normal; background-color: lightyellow;"></td>
                <td class="ipt_tdhr" style="font-weight: normal; background-color: lightyellow;"></td>
              </tr>
            </tbody>
            <tbody>
              <tr
                v-for="(item, idx) in filteredItems"
                v-bind:key="item.id || idx"
              >
                <td class="ipt_tdr">
                  <b-link :to="`/sales/viewsale/${item.invoice_id}/`" target="_blank">
                    {{ item.invoice_id }}
                  </b-link>
                </td>
                <td class="ipt_tdr">{{ item.product_sku }}</td>
                <td class="ipt_tdr">{{ item.product_name || 'N/A' }}</td>
                <td class="ipt_tdr">{{ item.category_name || 'N/A' }}</td>
                <td class="ipt_tdr">{{ item.vendor || 'N/A' }}</td>
                <td class="ipt_tdr" style="text-align: center;">{{ item.quantity || 0 }}</td>
                <td class="ipt_tdr" style="text-align: right;">
                  ${{ parseFloat(item.selling_price || 0).toFixed(2) }}
                </td>
                <td class="ipt_tdr" style="text-align: right; font-weight: bold;">
                  ${{ (parseFloat(item.selling_price || 0) * parseInt(item.quantity || 0)).toFixed(2) }}
                </td>
                <td class="ipt_tdr">{{ item.added_by || 'N/A' }}</td>
                <td class="ipt_tdr">{{ formatDate(item.added_at) }}</td>
                <td class="ipt_tdr" style="text-align: center;">
                  <div v-if="item.image_url && item.image_url !== 'null' && item.image_url !== ''" 
                       style="width: 50px; height: 50px; cursor: pointer;"
                       @click="showFullImage(item)">
                    <img 
                      :src="item.image_url" 
                      style="max-width: 100%; max-height: 50px; object-fit: contain;"
                      @error="handleImageError($event, item)"
                      alt="Product image"
                    />
                  </div>
                  <span v-else class="text-muted">No Image</span>
                </td>
                <td class="ipt_tdr" style="text-align: center;">
                  <b-button
                    v-if="packet.userDetails && packet.userDetails.userrole == 'admin'"
                    size="sm"
                    variant="danger"
                    @click="deleteItem(item)"
                    title="Delete this non-inventory item"
                  >
                    <b-icon-trash></b-icon-trash>
                  </b-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-card>

    <!-- Image Viewer Modal -->
    <b-modal id="modal-image-viewer" size="lg" hide-footer centered title="Product Image">
      <div class="text-center">
        <b-img
          :src="currentFullSizeImage"
          fluid
          alt="Full size product image"
          style="max-height: 70vh; object-fit: contain;"
        />
        <div class="mt-3">
          <strong>{{ currentProductName }}</strong>
        </div>
      </div>
      <template #modal-footer>
        <b-button size="sm" variant="secondary" @click="$bvModal.hide('modal-image-viewer')">
          Close
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import moment from "moment";
import Requestor from "@/utilities/Requestor";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import { BIconFunnelFill, BIconTrash } from "bootstrap-vue";

// Import Lightpick for date range picker
const Lightpick = require("lightpick");
import "lightpick/css/lightpick.css";

export default {
  name: "NonInventoryItems",
  components: { Loading },
  
  data: function() {
    return {
      items: [],
      packet: { userDetails: {} },
      items_being_loaded: false,
      search_txt: "",
      picked_category: null,
      picked_vendor: null,
      start_date: null,
      end_date: null,
      date_range: "",
      sort_asc: false,
      filtersVisible: false,
      isMobile: false,
      currentFullSizeImage: null,
      currentProductName: null,
    };
  },

  computed: {
    // Filter items based on search and filters
    filteredItems() {
      let that = this;
      return this.items.filter(function(item) {
        let status = true;

        // Search filter
        if (that.search_txt) {
          const searchLower = that.search_txt.toLowerCase();
          const matchSku = (item.product_sku || '').toLowerCase().includes(searchLower);
          const matchName = (item.product_name || '').toLowerCase().includes(searchLower);
          const matchInvoice = (item.invoice_id || '').toLowerCase().includes(searchLower);
          const matchVendor = (item.vendor || '').toLowerCase().includes(searchLower);
          
          if (!matchSku && !matchName && !matchInvoice && !matchVendor) {
            status = false;
          }
        }

        // Category filter
        if (that.picked_category && item.category_name !== that.picked_category) {
          status = false;
        }

        // Vendor filter
        if (that.picked_vendor && item.vendor !== that.picked_vendor) {
          status = false;
        }

        return status;
      });
    },

    // Total quantity of filtered items
    totalQuantity() {
      return this.filteredItems.reduce((sum, item) => sum + parseInt(item.quantity || 0), 0);
    },

    // Total value of filtered items
    totalValue() {
      return this.filteredItems.reduce((sum, item) => {
        return sum + (parseFloat(item.selling_price || 0) * parseInt(item.quantity || 0));
      }, 0);
    },

    // Category options for filter
    categoryOptions() {
      let cats = this.items.map(d => d.category_name).filter(Boolean);
      return [...new Set(cats)].map(c => ({ value: c, text: c }));
    },

    // Vendor options for filter
    vendorOptions() {
      let vendors = this.items.map(d => d.vendor).filter(Boolean);
      return [...new Set(vendors)].map(v => ({ value: v, text: v }));
    }
  },

  methods: {
    // =====================================================
    // FETCH NON-INVENTORY ITEMS
    // =====================================================
    fetchNonInventoryItems: function() {
      let path = "/api/inventory/get_non_inventory_items/";
      let that = this;
      
      let data = {};
      if (that.start_date && that.end_date) {
        data.start_date = that.start_date;
        data.end_date = that.end_date;
      }
      
      let s = function(response) {
        if (response.data && response.data.result) {
          // Parse numeric values from strings
          that.items = response.data.result.map(item => ({
            ...item,
            quantity: parseInt(item.quantity) || 0,
            selling_price: parseFloat(item.selling_price) || 0,
            total_price: parseFloat(item.total_price) || 0
          }));
          console.log("Non-inventory items loaded:", that.items.length);
        } else {
          that.$bvToast.toast(response.data.error || "Error loading items", {
            title: "Error",
            autoHideDelay: 1500,
            variant: "warning"
          });
        }
        that.items_being_loaded = false;
      };

      let f = function(error) {
        console.error("Error fetching non-inventory items:", error);
        that.$bvToast.toast("Error retrieving non-inventory items.", {
          title: "Error",
          autoHideDelay: 1500,
          variant: "danger"
        });
        that.items_being_loaded = false;
      };

      this.items_being_loaded = true;
      this.items = [];
      Requestor.getSyncP(path, data, this, s, f);
    },

    // =====================================================
    // DATE RANGE HANDLING
    // =====================================================
    clearDateFilter: function() {
      this.date_range = "";
      this.start_date = null;
      this.end_date = null;
      this.fetchNonInventoryItems();
    },

    onDateRangeChange() {
      if (this.date_range && this.date_range.includes("-")) {
        const [start, end] = this.date_range
          .split(" - ")
          .map(d => moment(d, "DD/MM/YYYY").format("YYYY-MM-DD"));
        this.start_date = start;
        this.end_date = end;
      } else {
        this.start_date = null;
        this.end_date = null;
      }
      
      this.fetchNonInventoryItems();
    },

    // =====================================================
    // SORTING
    // =====================================================
    sortby: function(key) {
      if (this.sort_asc) {
        this.items = Requestor.sort(this.items, key);
      } else {
        this.items = Requestor.sort_reverse(this.items, key);
      }
      this.sort_asc = !this.sort_asc;
    },

    // =====================================================
    // EXCEL EXPORT
    // =====================================================
    downloadExcel: function() {
      let filtered = this.filteredItems;

      if (filtered.length === 0) {
        this.$bvToast.toast("No data to export!", {
          title: "Info",
          autoHideDelay: 1500,
          variant: "info"
        });
        return;
      }

      let ws_data = filtered.map(item => ({
        Invoice: item.invoice_id,
        SKU: item.product_sku,
        Product: item.product_name || 'N/A',
        Category: item.category_name || 'N/A',
        Vendor: item.vendor || 'N/A',
        Quantity: item.quantity || 0,
        Price: parseFloat(item.selling_price || 0).toFixed(2),
        Total: (parseFloat(item.selling_price || 0) * parseInt(item.quantity || 0)).toFixed(2),
        'Added By': item.added_by || 'N/A',
        'Added At': this.formatDate(item.added_at)
      }));

      function escapeCSV(value) {
        if (value === null || value === undefined) return "";
        value = value.toString();
        if (/[",\n]/.test(value)) {
          value = '"' + value.replace(/"/g, '""') + '"';
        }
        return value;
      }

      let headers = Object.keys(ws_data[0])
        .map(escapeCSV)
        .join(",");
      let rows = ws_data
        .map(obj =>
          Object.values(obj)
            .map(escapeCSV)
            .join(",")
        )
        .join("\n");

      let csvContent = "data:text/csv;charset=utf-8," + headers + "\n" + rows;

      let encodedUri = encodeURI(csvContent);
      let link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", "non_inventory_items_export.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    // =====================================================
    // DELETE ITEM
    // =====================================================
    deleteItem: function(item) {
      let that = this;
      
      this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete non-inventory item "${item.product_sku}"?`,
        {
          title: 'Delete Item',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'DELETE',
          cancelTitle: 'CANCEL',
          footerClass: 'p-2',
          centered: true
        }
      ).then(value => {
        if (value) {
          let path = "/api/inventory/delete_non_inventory_item/";
          let data = {
            id: item.id,
            invoice_id: item.invoice_id,
            username: that.packet.userDetails.username
          };
          
          let s = function(response) {
            if (response.data && response.data.error) {
              that.$bvToast.toast(response.data.error, {
                title: "Error",
                autoHideDelay: 1500,
                variant: "danger"
              });
            } else {
              that.$bvToast.toast("Item deleted successfully!", {
                title: "Success",
                autoHideDelay: 1500,
                variant: "success"
              });
              // Refresh the list
              that.fetchNonInventoryItems();
            }
          };
          
          let f = function(error) {
            console.error("Error deleting item:", error);
            that.$bvToast.toast("Error deleting item. Please try again.", {
              title: "Error",
              autoHideDelay: 1500,
              variant: "danger"
            });
          };
          
          Requestor.postSyncP(path, data, this, s, f);
        }
      });
    },

    // =====================================================
    // IMAGE HANDLING
    // =====================================================
    showFullImage: function(item) {
      if (item.image_url && item.image_url !== 'null' && item.image_url !== '') {
        this.$bvModal.show('modal-image-viewer');
        this.$nextTick(() => {
          this.currentFullSizeImage = item.image_url;
          this.currentProductName = item.product_name || item.product_sku || 'Product Image';
        });
      }
    },

    handleImageError: function(event, item) {
      event.target.style.display = 'none';
      const parent = event.target.parentElement;
      if (parent) {
        parent.innerHTML = '<span class="text-muted">No Image</span>';
      }
    },

    // =====================================================
    // UTILITY METHODS
    // =====================================================
    formatDate: function(date) {
      if (!date) return 'N/A';
      return moment(date).format('MM/DD/YYYY hh:mm A');
    },

    handleResize: function() {
      this.isMobile = window.innerWidth < 768;
    },

    initializeUser: function() {
      try {
        const packetData = localStorage.packet || "{}";
        this.packet = JSON.parse(packetData);
        this.packet.userDetails = JSON.parse(localStorage.userDetails || "{}");
      } catch (err) {
        console.error("Error parsing user data:", err);
        this.packet = { userDetails: {} };
      }
    }
  },

  created: function() {
    this.initializeUser();
    this.fetchNonInventoryItems();
  },

  mounted: function() {
    this.handleResize();
    window.addEventListener("resize", this.handleResize);

    // Initialize Lightpick date picker
    let that = this;
    try {
      var picker = new Lightpick({
        field: document.getElementById("dates"),
        singleDate: false,
        repick: true,
        onSelect: function(start, end) {
          that.start_date = start
            ? start.startOf("day").format("YYYY-MM-DD")
            : null;
          that.end_date = end ? end.endOf("day").format("YYYY-MM-DD") : null;
          
          if (start && end) {
            that.date_range = `${start.format("DD/MM/YYYY")} - ${end.format("DD/MM/YYYY")}`;
          } else {
            that.date_range = "";
          }
          
          that.fetchNonInventoryItems();
        }
      });
    } catch (e) {
      console.warn("Lightpick initialization failed:", e);
    }
  },

  beforeDestroy: function() {
    window.removeEventListener("resize", this.handleResize);
  }
};
</script>

<style scoped>
.ipt_tdhr {
  font-weight: bold;
  font-size: 12px;
  border: 1px solid #ddd;
  padding: 8px 6px;
  text-align: left;
  cursor: pointer;
  background: #f8f9fa;
}

.ipt_tdr {
  font-weight: normal;
  font-size: 12px;
  border: 1px solid #ddd;
  padding: 6px;
  text-align: left;
}

.ipt_tdr a {
  color: #2c5aa0;
  text-decoration: none;
}

.ipt_tdr a:hover {
  text-decoration: underline;
}

.summary-stats {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  border: 1px solid #e9ecef;
}

.stat-box {
  text-align: center;
  padding: 5px;
}

.stat-label {
  font-size: 13px;
  color: #6c757d;
  display: block;
}

.stat-value {
  font-size: 20px;
  font-weight: bold;
  color: #2c5aa0;
  display: block;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .summary-stats .col {
    margin-bottom: 10px;
  }
  
  .stat-value {
    font-size: 16px;
  }
  
  .ipt_tdhr, .ipt_tdr {
    font-size: 11px;
    padding: 4px;
  }
}

/* Hover effect for table rows */
tbody tr:hover {
  background-color: #f0f7ff !important;
}
</style>


// WEBPACK FOOTER //
// src/components/Inventory/NonInventoryItems.vue